import { FieldArray, FormikErrors, FormikProvider, useFormik } from "formik";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { ApiPaths } from "../../api/paths";
import GridDetailItem from "../../components/grid-detail-item/GridDetailItem";
import DeleteIcon from "../../components/icons/DeleteIcon";
import useData from "../../hooks/use-data";
import useDisclosure from "../../hooks/use-disclosure";
import useMutateData from "../../hooks/use-mutate-data";
import { GridItemType } from "../../types/common";
import { CoverType } from "../../types/cover";
import { DocumentType } from "../../types/document";
import { toBase64File } from "../../utils/to-base64-file";
import { getStringDate } from "../../utils/utc-date-conversion";
import ClaimSuccessModal from "./claim-success-modal/ClaimSuccessModal";
import "./raise-new-claim.css";

interface InitialValues {
  claim_amount: string;
  currency: string;
  documents: DocumentType[];
}

interface DocumentsObject {
  [key: string]: string;
}

export default function RaiseNewClaim() {
  const { coverId } = useParams();
  const { show, onHide, onShow } = useDisclosure();
  const { t } = useTranslation(["common", "raiseNewClaim"]);
  const [documents, setDocuments] = useState<DocumentsObject>({});
  const { mutate } = useMutateData(ApiPaths.claims, {
    onError: () => {
      toast(t("error.raise", { ns: "raiseNewClaim" }), {
        type: "error",
        toastId: "raise-claim",
      });
    },
    onSuccess: () => {
      toast(t("success.raise", { ns: "raiseNewClaim" }), { type: "success" });
      onShow();
    },
  });
  const { data } = useData<CoverType>(`${ApiPaths.covers}/${coverId}`);
  const canSubmit = useMemo(
    () => data?.cover_status === "ACTIVE" || data?.cover_status === "EXPIRING",
    [data]
  );
  const initialValues: InitialValues = {
    claim_amount: "",
    currency: "",
    documents: [],
  };
  const baseDocument = {
    document_name: "",
    document: "",
  };
  const validationSchema = Yup.object().shape({
    claim_amount: Yup.string().required(
      t("validations.claim_amount", { ns: "raiseNewClaim" }) ?? ""
    ),
    currency: Yup.string().required(
      t("validations.currency", { ns: "raiseNewClaim" }) ?? ""
    ),
    documents: Yup.array()
      .of(
        Yup.object().shape({
          document_name: Yup.string().required(
            t("validations.document_name", { ns: "raiseNewClaim" }) ?? ""
          ),
          document: Yup.string().required(
            t("validations.document", { ns: "raiseNewClaim" }) ?? ""
          ),
        })
      )
      .min(1)
      .required(t("validations.documents", { ns: "raiseNewClaim" }) ?? ""),
  });
  const formik = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      const temp = { ...values };
      temp.documents = temp.documents.map((doc, index) => {
        return {
          ...doc,
          document: documents[index],
        };
      });
      mutate(values);
    },
  });

  const cover_details_map: GridItemType[] = [
    {
      accessor: "product",
      label: t("details.label.cover.cover", { ns: "myCovers" }),
      value: (val) => val?.product_name,
    },
    {
      accessor: "product",
      label: t("details.label.cover.id", { ns: "myCovers" }),
      value: (val) => val?.product_name,
    },
    {
      accessor: "product",
      label: t("details.label.cover.product_name", { ns: "myCovers" }),
      value: (val) => val?.product_name,
    },
    {
      accessor: "product",
      label: t("details.label.cover.product_type", { ns: "myCovers" }),
      value: (val) => val?.product_name,
    },
    {
      accessor: "period",
      label: t("details.label.cover.period", { ns: "myCovers" }),
      value: (val) => `${val} days`,
    },
    {
      accessor: "cover_amount",
      label: t("details.label.cover.amount", { ns: "myCovers" }),
      value: (val) => (
        <p>
          {new Intl.NumberFormat("en-us", {
            style: "currency",
            currency: "USD",
            maximumFractionDigits: 0,
          }).format(val)}
        </p>
      ),
    },
    {
      accessor: "cover_start_date",
      label: t("details.label.cover.start_date", { ns: "myCovers" }),
      value: (val) => getStringDate(val),
    },
    {
      accessor: "cover_premium_amount",
      label: t("details.label.cover.premium", { ns: "myCovers" }),
      value: (val) => (
        <p>
          {new Intl.NumberFormat("en-us", {
            style: "currency",
            currency: "USD",
            maximumFractionDigits: 0,
          }).format(val)}
        </p>
      ),
    },
    {
      accessor: "cover_end_date",
      label: t("details.label.cover.end_date", { ns: "myCovers" }),
      value: (val) => getStringDate(val),
    },
  ];

  const isDocumentsFieldError = (
    fieldName: "document_name" | "document",
    index: number
  ) => {
    if (
      formik?.touched?.documents &&
      formik.touched.documents[index] &&
      typeof formik.touched.documents[index] === "object" &&
      formik.touched.documents[index][fieldName] &&
      formik.errors &&
      formik.errors.documents &&
      typeof formik.errors.documents !== "string" &&
      formik.errors.documents[index] &&
      typeof formik.errors.documents[index] !== "string" &&
      (formik.errors.documents[index] as FormikErrors<DocumentType>)[fieldName]
    ) {
      return (formik.errors.documents[index] as FormikErrors<DocumentType>)[
        fieldName
      ];
    }
    return "";
  };

  return (
    <form className="p-3 pb-5 p-md-4 mb-5" onSubmit={formik.handleSubmit}>
      <ClaimSuccessModal show={show} onHide={onHide} />
      <div className="d-flex align-items-center justify-content-between mb-3 ">
        <h2 className="primary-title">
          {t("heading.raise", { ns: "raiseNewClaim" })}
        </h2>
        <div className="d-flex justify-content-end">
          <button
            className="primary-button "
            type="submit"
            disabled={!canSubmit}
          >
            {t("button.submit", { ns: "common" })}
          </button>
        </div>
      </div>
      <div className="px-3 py-3 px-md-5 py-md-4 bg-white rounded-2 mb-3 box-shadow">
        <div className="claim-details-status d-flex justify-content-between align-items-center pb-3 mb-3 ">
          <h2 className="primary-title font-14">
            {t("heading.claim", { ns: "raiseNewClaim" })}
          </h2>
          {data?.cover_status && (
            <div>
              <p className="d-none d-md-inline secondary-color font-14 me-1">
                {t("label.claim.status", { ns: "raiseNewClaim" })}:
              </p>
              <span className=" status-button info-required-status py-2 px-3 fw-bold">
                {data?.cover_status}
              </span>
            </div>
          )}
        </div>
        <div className="input-group mb-3" style={{ maxWidth: "350px" }}>
          <input
            type="text"
            className={`form-control ${
              formik.touched.claim_amount && formik.errors.claim_amount
                ? "is-invalid"
                : ""
            }`}
            placeholder={
              t("placeholder.claim.amount", { ns: "raiseNewClaim" }) as string
            }
            {...formik.getFieldProps("claim_amount")}
          />
          <select
            className="form-select"
            style={{ maxWidth: "100px" }}
            {...formik.getFieldProps("currency")}
          >
            <option value="ETH">ETH</option>
            <option value="USD">USD</option>
          </select>
          {formik.touched.claim_amount && formik.errors.claim_amount && (
            <div className="invalid-feedback">{formik.errors.claim_amount}</div>
          )}
        </div>
        {!canSubmit && (
          <p className="mb-3 font-14">
            <strong>{t("label.note", { ns: "common" })}: </strong>
            {t("message.condition", { ns: "raiseNewClaim" })}
          </p>
        )}
      </div>
      <FormikProvider value={formik}>
        <FieldArray
          name="documents"
          render={(arrayHelpers) => (
            <div className="claim-details-table px-3 py-3 px-md-5 py-md-4 bg-white box-shadow rounded-2 mb-3">
              <div className="claim-details-status pb-3 mb-3 ">
                <div className="d-flex justify-content-between align-items-center">
                  <h2 className="primary-title font-14">
                    {t("heading.evidence", { ns: "raiseNewClaim" })}
                  </h2>
                  <button
                    className="primary-button"
                    type="button"
                    onClick={() => arrayHelpers.push(baseDocument)}
                    disabled={false}
                  >
                    {t("button.add", { ns: "common" })}
                  </button>
                </div>
              </div>
              {typeof formik.errors.documents === "string" && (
                <div className="text-danger mb-3">
                  {formik.errors.documents}
                </div>
              )}
              <div className="evidence-table">
                <table className="table box-shadow">
                  <thead className="">
                    <tr className="table-header claims-table-header">
                      <th className="align-middle px-3 py-2">
                        {t("table_head.evidence.name", { ns: "raiseNewClaim" })}
                      </th>
                      <th className="align-middle px-3 py-2">
                        {t("table_head.evidence.type", { ns: "raiseNewClaim" })}
                      </th>
                      <th className="align-middle px-3 py-2">
                        {t("table_head.evidence.status", {
                          ns: "raiseNewClaim",
                        })}
                      </th>
                      <th className="align-middle px-3 py-2">
                        {t("table_head.action", {
                          ns: "common",
                          count: 3,
                        })}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {formik.values.documents.length > 0 ? (
                      formik.values.documents.map((values, index) => {
                        return (
                          <tr key={index}>
                            <td className="align-middle font-16 px-3 py-2">
                              <input
                                type="file"
                                className={`form-control form-control-sm ${
                                  isDocumentsFieldError("document", index)
                                    ? "is-invalid"
                                    : ""
                                }`}
                                onChange={(e) => {
                                  if (e.target.files) {
                                    formik.setFieldValue(
                                      `documents.${index}.document`,
                                      e.target.files[0]
                                    );
                                    toBase64File(e.target.files[0], (f) =>
                                      setDocuments((d) => ({
                                        ...d,
                                        [index]: f,
                                      }))
                                    );
                                  }
                                }}
                              />
                              {isDocumentsFieldError("document", index) && (
                                <div className="invalid-feedback fw-normal my-1">
                                  {isDocumentsFieldError("document", index)}
                                </div>
                              )}
                            </td>
                            <td className="align-middle font-16 text-black fw-bold  px-3 py-2">
                              <select
                                className={`form-select form-select-sm ${
                                  isDocumentsFieldError("document_name", index)
                                    ? "is-invalid"
                                    : ""
                                }`}
                                {...formik.getFieldProps(
                                  `documents.${index}.document_name`
                                )}
                              >
                                <option value="">
                                  {t("placeholder.document", {
                                    ns: "raiseNewClaim",
                                  })}
                                </option>
                                <option value="PASSPORT">Passport</option>
                                <option value="ID">ID</option>
                                <option value="OTHER">Other</option>
                              </select>
                              {isDocumentsFieldError(
                                "document_name",
                                index
                              ) && (
                                <div className="invalid-feedback fw-normal my-1">
                                  {isDocumentsFieldError(
                                    "document_name",
                                    index
                                  )}
                                </div>
                              )}
                            </td>

                            {formik.values.documents[index].document_status ? (
                              <td className="align-middle ">
                                <span className="status-button pending-status px-3 py-2">
                                  {
                                    formik.values.documents[index]
                                      .document_status
                                  }
                                </span>
                              </td>
                            ) : (
                              <td className="align-middle font-16 text-black fw-bold  px-3 py-2">
                                <span>-</span>
                              </td>
                            )}

                            <td className="align-middle py-2">
                              <div className="d-flex gap-2 align-items-center">
                                <button
                                  className="btn font-14 ff-raleway d-flex align-items-center"
                                  type="button"
                                  onClick={() => {
                                    arrayHelpers.remove(index);
                                    setDocuments((d) => {
                                      const temp = { ...d };
                                      delete temp[index];
                                      return temp;
                                    });
                                  }}
                                >
                                  <span className="me-2">
                                    <DeleteIcon />
                                  </span>
                                  {t("button.delete", { ns: "common" })}
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td
                          colSpan={4}
                          className="py-2 align-middle text-center"
                        >
                          {t("message.no_documents", { ns: "raiseNewClaim" })}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        />
      </FormikProvider>
      {true && (
        <div className="box-shadow px-3 py-3 px-md-5 py-md-4 bg-white rounded-2 mb-3">
          <div className="claim-details-status d-flex justify-content-between pb-3 mb-3 ">
            <h2 className="primary-title font-14 ">
              {t("heading.cover", { ns: "raiseNewClaim" })}
            </h2>
            <div>
              <p className="d-none d-md-inline secondary-color font-14 me-1">
                {t("label.cover.status", { ns: "raiseNewClaim" })}:
              </p>
              <span
                className={`status-button ${data?.cover_status?.toLowerCase()}-status py-2 px-3 fw-bold`}
              >
                {data?.cover_status}
              </span>
            </div>
          </div>
          <div className="client-details client-details-cont d-grid">
            {cover_details_map.map((item) => {
              const val = item.accessor;
              return (
                <GridDetailItem
                  key={item.accessor}
                  label={item.label}
                  data={item.value(data && data[val as keyof CoverType])}
                />
              );
            })}
          </div>
        </div>
      )}
    </form>
  );
}
